import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { AuthContext } from '../../contexts/AuthContext';
import { paymentMethodService } from '../../services/paymentMethodService';
import Dialog from '../common/Dialog';
import UpgradeBar from '../common/UpgradeBar';

const Container = styled.div`
  padding: 24px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Title = styled.h1`
  font-size: 36px;
  margin-bottom: 24px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const Form = styled.form`
  display: grid;
  gap: 32px;
  max-width: 800px;
  background: var(--surface);
  padding: 32px;
  border-radius: 20px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
`;

const Required = styled.span`
  color: var(--error);
  font-size: 18px;
  line-height: 1;
`;

const Input = styled.input`
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--shadow-md);
  }

  &:hover {
    border-color: var(--primary-light);
  }
`;

const Select = styled.select`
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--shadow-md);
  }

  &:hover {
    border-color: var(--primary-light);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  ${props => props.primary && `
    background: var(--gradient-primary);
    color: white;
    border: none;
    box-shadow: var(--shadow-md);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }
  `}
  
  ${props => props.secondary && `
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    
    &:hover {
      background: var(--surface-hover);
      transform: translateY(-2px);
      border-color: var(--primary-light);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const FormDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const CreatePaymentMethod = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'credit_card',
    details: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);

      // Validate form data
      if (!formData.name.trim()) {
        throw new Error('Name is required');
      }

      if (!formData.type) {
        throw new Error('Type is required');
      }

      const newPaymentMethod = {
        user_id: user.id,
        name: formData.name.trim(),
        type: formData.type,
        details: formData.details.trim()
      };

      await paymentMethodService.createPaymentMethod(newPaymentMethod);

      // Show success dialog instead of alert
      setShowSuccess(true);
    } catch (error) {
      console.error('Error creating payment method:', error);
      alert(error.message || 'Failed to create payment method. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    // Navigate back with refresh flag
    navigate('/payment-methods', { 
      replace: true,
      state: { refresh: true }
    });
  };

  return (
    <Layout>
      <Container>
        <UpgradeBar />
        <Title>Create Payment Method</Title>
        <FormDescription>
          Add a new payment method to track your subscription payments effectively.
        </FormDescription>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Name<Required>*</Required></Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter payment method name"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Type<Required>*</Required></Label>
            <Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="credit_card">Credit Card</option>
              <option value="debit_card">Debit Card</option>
              <option value="bank_account">Bank Account</option>
              <option value="paypal">PayPal</option>
              <option value="other">Other</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Details</Label>
            <Input
              type="text"
              name="details"
              value={formData.details}
              onChange={handleChange}
              placeholder="Enter additional details (optional)"
            />
          </FormGroup>

          <ButtonGroup>
            <Button 
              type="submit" 
              primary 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Save & Exit'}
            </Button>
            <Button 
              type="button" 
              secondary 
              onClick={() => navigate('/payment-methods')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Form>

        <Dialog
          isOpen={showSuccess}
          onClose={handleSuccessClose}
          title="Success! 💳"
          message="Your payment method has been created successfully!"
          icon="✨"
        />
      </Container>
    </Layout>
  );
};

export default CreatePaymentMethod; 